import type { RequestStatus } from '@gestion-permisos/contracts';

/** Estados en el orden del flujo, con su nombre en pantalla. */
export const REQUEST_STATUSES: readonly RequestStatus[] = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'CANCELLED'];

export const STATUS_LABELS: Record<RequestStatus, string> = {
  DRAFT: 'Borrador',
  PENDING_APPROVAL: 'Pendiente de aprobación',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Cancelada',
};

/** Texto de las tarjetas de conteo. «Pendiente» cabe en el recuadro; el resto de la interfaz usa `STATUS_LABELS`. */
export const COUNT_LABELS: Record<RequestStatus, string> = {
  ...STATUS_LABELS,
  PENDING_APPROVAL: 'Pendiente',
};

/**
 * Fecha civil `YYYY-MM-DD` → `DD/MM/YYYY`, **como texto** (plan 11 §4.6): nunca se convierte a `Date`,
 * para que ninguna zona horaria desplace el día. Un valor inesperado se muestra tal cual.
 */
export function formatCivilDate(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : date;
}

export const BUSINESS_TIME_ZONE = 'America/Bogota';

const INSTANT_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Instante ISO en UTC (`…Z`) → `DD/MM/YYYY HH:mm` en America/Bogota (D-09). */
export function formatInstant(instant: string): string {
  const parts = Object.fromEntries(INSTANT_FORMAT.formatToParts(new Date(instant)).map((part) => [part.type, part.value]));
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
}

const TODAY_FORMAT = new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });

/** Fecha civil (`YYYY-MM-DD`) de un instante en America/Bogota (D-09); por defecto, hoy. No depende de la zona del navegador. */
export function todayCivil(now: Date = new Date()): string {
  const parts = Object.fromEntries(TODAY_FORMAT.formatToParts(now).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

const HOURS_FORMAT = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 });

/** Horas tal como las calcula la API (no se recalculan en el cliente). */
export function formatHours(hours: number): string {
  return `${HOURS_FORMAT.format(hours)} h`;
}
